import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, Outlet } from 'react-router-dom'
import { SessionController, DelayController } from '../../component'
import Loading from '../../pages/Loading/Loading'
import { parseParam, setCaptchaBypassKey } from '../../util'
import { handleTarget } from './utils'
import {
  BFF_STATUS,
  PARAM_LOGIN_HINT,
  PARAM_EMAIL,
  PARAM_FIRSTNAME,
  PARAM_LASTNAME,
  PARAM_ALLOW_RETURN,
  PARAM_TARGET,
  PARAM_COUNTRY,
  PARAM_HIDE_CREATE,
  PARAM_FLOW_TOKEN,
  PARAM_REMEMBER_ME,
  PARAM_CSRF_TOKEN,
  PARAM_CAPTCHA_BYPASS_KEY,
  PARAM_AUTOREDIRECT,
  PARAM_USERNAME_TYPE,
  USER_INTERACTION_TIMEOUT,
  TRACKED_ROUTES,
  PARAM_OTP,
  PARAM_LOCALE,
  PARAM_FLOW_ID,
  PARAM_THEME_ID
} from '../../constants'

function useQuery() {
  const a = useLocation().search
  return new URLSearchParams(a)
}

function getFieldParams(query) {
  return [
    PARAM_LOGIN_HINT,
    PARAM_EMAIL,
    PARAM_FIRSTNAME,
    PARAM_LASTNAME,
    PARAM_ALLOW_RETURN,
    PARAM_TARGET,
    PARAM_COUNTRY,
    PARAM_HIDE_CREATE,
    PARAM_FLOW_TOKEN,
    PARAM_CSRF_TOKEN,
    PARAM_REMEMBER_ME,
    PARAM_AUTOREDIRECT,
    PARAM_USERNAME_TYPE,
    PARAM_LOCALE,
    PARAM_FLOW_ID
  ].reduce((params, key) => {
    // Don't remove the checker here anymore.
    if (query.get(key) !== null) {
      params[key] = parseParam(key, query.get(key))
    }
    return params
  }, {})
}

const Main = ({ children }) => {
  const query = useQuery()
  const location = useLocation()
  const modes = ['light', 'dark', 'omen']

  let timer
  const runExpiredInteractionTimer = () => {
    if (TRACKED_ROUTES.includes(location.pathname)) {
      dispatch.user.update({ expiredInteraction: false })
      timer = window.setTimeout(() => {
        dispatch.user.update({ expiredInteraction: true })
      }, USER_INTERACTION_TIMEOUT)
    }
  }

  const handleListenerEvent = () => {
    clearTimeout(timer)
    runExpiredInteractionTimer()
  }

  useEffect(() => {
    window.addEventListener('click', handleListenerEvent)
    return () => {
      window.removeEventListener('click', handleListenerEvent)
    }
    // eslint-disable-next-line
  }, [location.pathname])

  const dispatch = useDispatch()
  const [isLoading, setLoading] = useState(true)
  const flowToken = useSelector((state) => state.user[PARAM_FLOW_TOKEN])
  const flowIdToken = useSelector((state) => state.user[PARAM_FLOW_ID])
  const storedFlow = flowToken || flowIdToken

  useEffect(() => {
    async function init() {
      const FLOW_TOKEN_FROM_URL = query.get(PARAM_FLOW_TOKEN)
      const FLOW_ID_FROM_URL = query.get(PARAM_FLOW_ID)
      const hasFlowParam = FLOW_TOKEN_FROM_URL || FLOW_ID_FROM_URL
      const isNewSession = hasFlowParam && hasFlowParam !== storedFlow

      const arkoseBypassKey =
        isNewSession && query.has(PARAM_CAPTCHA_BYPASS_KEY) && query.get(PARAM_CAPTCHA_BYPASS_KEY)
      if (arkoseBypassKey) setCaptchaBypassKey(arkoseBypassKey)

      dispatch.user.updateParams(getFieldParams(query))
      try {
        let data
        const initSessionParams = {}

        if (flowToken) initSessionParams.flow = flowToken
        if (flowIdToken) initSessionParams.flowId = flowIdToken
        // if the url has new values they will overwrite the previous set on flow and flowId
        if (query.has(PARAM_FLOW_ID)) initSessionParams.flowId = FLOW_ID_FROM_URL
        if (query.has(PARAM_FLOW_TOKEN)) initSessionParams.flow = FLOW_TOKEN_FROM_URL
        if (query.has(PARAM_OTP)) initSessionParams.otp = query.get(PARAM_OTP)

        // TODO: active line below when backend support for theme_id prop ready
        if (
          query.has(PARAM_THEME_ID) &&
          query.get(PARAM_THEME_ID).length &&
          !modes.includes(query.get(PARAM_THEME_ID))
        ) {
          initSessionParams[PARAM_THEME_ID] = query.get(PARAM_THEME_ID)
        }

        if (isNewSession) {
          data = await dispatch.session.initSessionWithFlow(initSessionParams)
        } else {
          data = await dispatch.session.fetchSessionInfo()
        }

        await dispatch.user.setCountry()
        await handleTarget({ data, query, dispatch })
        setLoading(false)
      } catch (errorData) {
        if (errorData.error === BFF_STATUS.REQUEST_TIMEOUT) {
          return // Keep user on loading page
        } else {
          dispatch.error.goError(errorData)
        }
        setLoading(false)
      }
    }
    init()
    // eslint-disable-next-line
  }, [])

  if (isLoading) return <Loading />

  return (
    <>
      <SessionController />
      <DelayController />
      <Outlet />
    </>
  )
}

export default Main
